import {Card, Icon} from '@atoms';
import {actions, getGroupById, useAppDispatch, useAppSelector, withAsyncThunkErrorHandling} from '@lib/store';
import {H3} from '@quarks';
import {Canvas, Fullbody, Heading} from '@templates';
import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {BreadCrumbs, useConnectBreadCrumbs} from './BreadCrumbs';
import {Button, GroupAvatar} from '@molecules';

import {GroupMembersSection} from './components/GroupMembersSection';
import {GroupPreferencesSection} from './components/GroupPreferencesSection';
import {useDialog, useModal} from '@hooks';
import {GroupInformationSelectionModal} from '../Connections/components/GroupInformationSelectionModal';
import toast from 'react-hot-toast';
import {useNavigate, useParams} from 'react-router-dom';

type Props = {};

export const GroupsSettingsPage = (props: Props) => {
  const {t} = useTranslation();
  const {openModal, addPages} = useModal();
  const {destructiveDialog} = useDialog();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {paths} = useConnectBreadCrumbs();
  const goBackPath = paths.at(-2) ?? '/connect';

  const {groupId} = useParams();
  const group = useAppSelector((state) => getGroupById(state, groupId || ''));

  const handleLeaveGroup = useCallback(async () => {
    if (!group) return;

    const confirmed = await destructiveDialog(
      t('groups:LeaveGroup.DialogTitle'),
      t('groups:LeaveGroup.DialogMessage'),
      undefined,
      t('groups:LeaveGroup.ConfirmButtonLabel'),
    );
    if (!confirmed) return;

    const res = await dispatch(withAsyncThunkErrorHandling(() => actions.removeSelfFromGroup(group.id)));

    navigate('/connect', {replace: true});
    if (res.success) {
      toast.success(t('groups:LeaveGroup.SuccessToast'));
    }
  }, [destructiveDialog, dispatch, group, navigate, t]);

  const handleDeleteGroup = useCallback(async () => {
    if (!group) return;

    const confirmed = await destructiveDialog(
      t('groups:DeleteGroup.DialogTitle'),
      t('groups:DeleteGroup.DialogMessage'),
      undefined,
      t('groups:DeleteGroup.ConfirmButtonLabel'),
    );
    if (!confirmed) return;

    const res = await dispatch(withAsyncThunkErrorHandling(() => actions.deleteGroup(group.id)));

    navigate('/connect', {replace: true});
    if (res.success) {
      toast.success(t('groups:DeleteGroup.SuccessToast'));
    }
  }, [destructiveDialog, dispatch, group, navigate, t]);

  return (
    group && (
      <>
        <Heading className=" px-4 justify-between">
          <div className=" col-span-full flex w-full grid-cols-2 justify-between items-center">
            <button
              onClick={() => {
                navigate(goBackPath);
              }}>
              <Icon
                icon="caretLeft"
                className="size-5"
              />
            </button>

            <h3>{t('groups:Settings')}</h3>
            <div className="size-5" />
          </div>
        </Heading>
        <Canvas />
        <Fullbody className="p-4 lg:p-8 md:py-14">
          <div className="md:flex justify-center flex-1">
            <div className="grid gap-4 sm:gap-[var(--gutterWidth)] grid-cols-1 md:grid-cols-[minmax(0,var(--maxWidth))] justify-center">
              <BreadCrumbs className="hidden sm:flex col-span-full " />

              <div className="grid gap-4 sm:gap-[var(--gutterWidth)] grid-cols-[minmax(0,var(--mainWidth))] col-span-full justify-center">
                <Card className="py-8 px-6 grid grid-cols-[auto_1fr_auto] items-center gap-4">
                  <GroupAvatar
                    group={group}
                    size="xlarge"
                  />
                  <div className="flex flex-col gap-1">
                    <H3>{group.name}</H3>
                  </div>
                  <Button
                    onClick={() => {
                      addPages([<GroupInformationSelectionModal group={group} />]);
                      openModal();
                    }}
                    button={'tertiary'}>
                    <Icon
                      icon="pencil"
                      className="size-4"
                    />
                    {t('Edit')}
                  </Button>
                </Card>

                <GroupMembersSection group={group} />

                <GroupPreferencesSection group={group} />

                <div className="grid grid-cols-2 gap-4">
                  <Button
                    button={'secondary destructive'}
                    size="full"
                    onClick={handleLeaveGroup}>
                    {t('groups:LeaveLabel')}{' '}
                    <Icon
                      icon="upload"
                      color="functional-red"
                      className="size-5 rotate-90"
                    />
                  </Button>
                  <Button
                    button={'secondary destructive'}
                    size="full"
                    onClick={handleDeleteGroup}>
                    {t('groups:DeleteLabel')}{' '}
                    <Icon
                      icon="trash"
                      color="functional-red"
                      className="size-5"
                    />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Fullbody>
      </>
    )
  );
};
